/**
 * Arabic display names for the curated AI tools directory.
 *
 * Why this lives in the frontend (and not in the CMS schema):
 *  - The tool catalog is curated and finite; brand transliterations are
 *    relatively stable.
 *  - Most international AI products have well-known Arabic transliterations
 *    that don't change.
 *  - Avoids a schema migration just for a display string.
 *
 * Usage:
 *  - The Tools page renders the Arabic name as a secondary title beneath the
 *    English brand name, only when an entry exists here.
 *  - The site search index ALSO consumes this map so Arabic-name queries
 *    (e.g. "تشات جي بي تي") return the right tool.
 *
 * Conventions applied below:
 *  - Brand transliteration into Arabic letters where the brand is the
 *    canonical name (ChatGPT → "تشات جي بي تي").
 *  - Short native-noun forms where the brand is descriptive
 *    (Image FX → "إيمج إف إكس").
 *  - Where a tool has multiple words, we keep spaces and don't translate
 *    common nouns like "Free", "AI", or "Pro" (those don't carry brand
 *    weight).
 *
 * Add new entries here when adding a tool to the catalog. Missing entries
 * gracefully render with no Arabic line on the card.
 */

const TOOL_NAMES_AR = {
  // General chat & assistants
  "chatgpt-free": "تشات جي بي تي",
  "google-gemini": "جوجل جيميناي",
  "microsoft-copilot": "مايكروسوفت كوبايلوت",
  kimi: "كيمي",
  deepseek: "ديب سيك",

  // Voice & English speaking
  hume: "هيوم",
  gliglish: "جليجليش",
  pi: "باي",
  "character-ai": "كاركتر دوت إيه آي",

  // Research & learning
  perplexity: "بربلكستي",
  notebooklm: "نوت بوك إل إم",
  "semantic-scholar": "سيمانتيك سكولر",
  consensus: "كونسنسوس",
  elicit: "إيلِيسِت",

  // PDF & document reading
  chatpdf: "تشات بي دي إف",
  openread: "أوبن ريد",
  explainpaper: "إكسبلِين بيبر",
  pdfgear: "بي دي إف جير",

  // Presentations & visual notes
  gamma: "جاما",
  napkin: "نابكِن",
  whimsical: "وِمزيكال",

  // Code & data
  "google-colab": "جوجل كولاب",
  "orange-data-mining": "أورنج لاستخراج البيانات",

  // Translation & language
  deepl: "ديب إل",
  reverso: "ريفِرسو",
  "immersive-translate": "إيمرسف ترانسليت",

  // Image generation
  ideogram: "آيديوجرام",
  leonardo: "ليوناردو",
  playground: "بلاي جراوند",
  "image-fx": "إيمج إف إكس",

  // Education
  magicschool: "ماجيك سكول",
  diffit: "دِفِت",
  conker: "كونكر",

  // VPN
  "proton-vpn-free": "بروتون في بي إن",
  "windscribe-free": "وِندسكرايب",
};

/** Returns the curated Arabic name for a tool slug, or "" if not mapped. */
export function getToolNameAr(slug) {
  if (!slug) return "";
  return TOOL_NAMES_AR[slug] || "";
}

export default TOOL_NAMES_AR;
